import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ApiError,
  deleteEvidence,
  listEvidence,
  listRegistry,
  listRetentionPolicies,
  listSectors,
  listStandards,
  fetchMark,
  readApplication,
  readDraft,
  readDurationGuardrails,
  saveDraft,
  submitApplication,
  uploadEvidence,
  uploadMark,
  validateApplication,
  type ApplicationDraft,
  type ApplicationValidation,
  type CertificationBody,
  type DurationGuardrails,
  type EvidenceEntry,
  type Sector,
} from "../api/client";
import { Advisories } from "./Advisories";
import { ApplicationSubmitted } from "./ApplicationSubmitted";
import { Stepper } from "./Stepper";
import { LegalIdentityStep } from "./steps/LegalIdentityStep";
import { AccreditationStep } from "./steps/AccreditationStep";
import { ImpartialityStep } from "./steps/ImpartialityStep";
import { PeopleStep } from "./steps/PeopleStep";
import { BrandStep } from "./steps/BrandStep";
import { PlatformStep } from "./steps/PlatformStep";
import { AgreementsStep } from "./steps/AgreementsStep";
import {
  blockingCount,
  blockingFor,
  EMPTY_APPLICATION,
  resultFor,
  setScopeSectors,
  stepComplete,
  STEPS,
  toggleEnabled,
  toggleSector,
  toggleStandard,
} from "./application";
import { idempotencyKey as issueIdempotencyKey } from "../api/idempotency";

/**
 * Certification body onboarding — the whole six-step application.
 *
 * Three things decide how this component behaves, and none of them is a preference.
 *
 * **The server owns whether a step is complete.** It is asked on every change, through
 * `POST /onboarding/certification-bodies/validation`, and asked again with the payload at
 * submission. Gating "Continue" in the browser alone would make requiredness something a
 * client can skip.
 *
 * **Blocking findings are held back until the applicant tries to leave the step.** Shouting
 * a required-field error at someone who has typed two characters trains them to ignore it.
 * The advisories, which are consequences rather than corrections, appear as soon as the
 * answer that causes them is given.
 *
 * **What has been typed is saved; what has been claimed is not.** The draft goes to its own
 * table, not into the sixteen nullable columns on the tenant — the administrator's register
 * reads `legal_name`, and a half-typed one would show there as a claim nobody made. The
 * evidence and the mark are different: those are content-addressed uploads, and they happen
 * as the files are chosen, because ADR 0013 means the tenant exists before the application
 * does.
 */
export function CertificationBodyOnboarding({ tenantId }: { tenantId: string }) {
  const [draft, setDraft] = useState<ApplicationDraft>(EMPTY_APPLICATION);
  const [validation, setValidation] = useState<ApplicationValidation | null>(null);
  const [step, setStep] = useState(0);
  const [furthest, setFurthest] = useState(0);
  const [attempted, setAttempted] = useState<ReadonlySet<number>>(new Set());
  const [loaded, setLoaded] = useState(false);

  const [countries, setCountries] = useState<string[]>([]);
  const [bodies, setBodies] = useState<string[]>([]);
  const [timezones, setTimezones] = useState<string[]>([]);
  const [retentionPolicies, setRetentionPolicies] = useState<string[]>([]);
  const [sectors, setSectors] = useState<Sector[]>([]);
  const [standards, setStandards] = useState<string[]>([]);
  const [guardrails, setGuardrails] = useState<DurationGuardrails | null>(null);

  const [evidence, setEvidence] = useState<EvidenceEntry[]>([]);
  const [evidenceBusy, setEvidenceBusy] = useState(false);
  const [evidenceFailure, setEvidenceFailure] = useState<string | null>(null);
  const [markPreview, setMarkPreview] = useState<string | null>(null);

  const [submitted, setSubmitted] = useState<CertificationBody | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitFailure, setSubmitFailure] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  /**
   * One key per attempt at *this* application, minted once.
   *
   * Regenerating it on a retry would defeat the header: the point of an idempotency key is
   * that the second request is recognisable as the same submission as the first, so a
   * network failure after the server committed replays the answer instead of conflicting.
   */
  const idempotencyKey = useRef(issueIdempotencyKey());

  // Selection lists and guardrails are administrator-maintained or regulatory data, so
  // they are fetched rather than compiled in — a country deactivated this morning must not
  // still be offered, and the MD 5 floor must not be a second copy of a number the engine
  // enforces.
  useEffect(() => {
    Promise.all([
      listRegistry("countries"),
      listRegistry("accreditation-bodies"),
      listRegistry("timezones"),
      listRetentionPolicies(),
      listSectors(),
      listStandards(),
      readDurationGuardrails(),
    ])
      .then(([country, body, zone, retention, sector, standard, rails]) => {
        setCountries(country);
        setBodies(body);
        setTimezones(zone);
        setRetentionPolicies(retention);
        setSectors(sector);
        setStandards(standard);
        setGuardrails(rails);
      })
      .catch((cause: unknown) => {
        setFailure(cause instanceof ApiError ? cause.message : "Reference data unavailable.");
      });
  }, []);

  /**
   * The two duration settings arrive seeded, and neither seed is invented here.
   *
   * The Stage 1 share is "seeded at 33%" in the specification and ADR 0001, and the
   * threshold starts at the MD 5 Cl. 2.1.2 floor — the loosest value a certification body
   * is permitted, which is the honest default to offer since anything below it is refused.
   * Both come from the server's reading of `reference-data/`. Only ever applied to a field
   * still blank, so a restored draft is never overwritten by a default.
   */
  useEffect(() => {
    // After the restore, so a seed is applied on top of a saved draft rather than racing
    // it — it fills a blank, and a restored answer is never a blank.
    if (!guardrails || !loaded) return;
    setDraft((current) => ({
      ...current,
      stage1_percentage:
        current.stage1_percentage || String(guardrails.stage1_default_percent),
      duration_threshold:
        current.duration_threshold || String(guardrails.threshold_floor_percent),
    }));
  }, [guardrails, loaded]);

  // What was typed before, what was attached before, and whether this was already
  // submitted. Until this settles nothing is saved back — an autosave racing the restore
  // would write an empty form over a finished one.
  useEffect(() => {
    Promise.all([readApplication(), readDraft(), listEvidence()])
      .then(([application, saved, attached]) => {
        if (application.onboarding_completed_at) {
          setSubmitted(application);
          return;
        }
        setEvidence(attached);
        if (saved) {
          setDraft({ ...EMPTY_APPLICATION, ...saved });
          // The bytes are on the server; a restored draft carries only the key.
          if (saved.brand_logo_key) void fetchMark(tenantId).then(showMark).catch(() => undefined);
        }
      })
      .catch((cause: unknown) => {
        setFailure(cause instanceof ApiError ? cause.message : "Could not read your application.");
      })
      .finally(() => setLoaded(true));
  }, [tenantId]);

  // The filenames the validator sees come from what is actually attached, not from a
  // second list the browser keeps. Two records of one fact is one too many.
  const attachedNames = useMemo(() => evidence.map((entry) => entry.filename), [evidence]);
  const forValidation = useMemo(
    () => ({ ...draft, evidence: attachedNames }),
    [draft, attachedNames],
  );

  // One validation in flight at a time, and a later draft always wins. Out-of-order
  // responses would otherwise show findings against text that has already been corrected.
  const generation = useRef(0);
  useEffect(() => {
    if (!loaded || submitted) return;
    const mine = ++generation.current;
    const timer = window.setTimeout(() => {
      validateApplication(forValidation)
        .then((result) => {
          if (generation.current === mine) setValidation(result);
        })
        .catch((cause: unknown) => {
          setFailure(cause instanceof ApiError ? cause.message : "Validation unavailable.");
        });
    }, 200);
    return () => window.clearTimeout(timer);
  }, [forValidation, loaded, submitted]);

  // Autosaved on the same rhythm. A failure here is deliberately quiet: losing a draft is
  // an inconvenience, and a red banner over a form someone is still typing into is worse.
  useEffect(() => {
    if (!loaded || submitted) return;
    const timer = window.setTimeout(() => {
      void saveDraft(draft).catch(() => undefined);
    }, 800);
    return () => window.clearTimeout(timer);
  }, [draft, loaded, submitted]);

  const definition = STEPS[step];
  const showBlocking = attempted.has(step);

  const set = useCallback((patch: Partial<ApplicationDraft>) => {
    setDraft((current) => ({ ...current, ...patch }));
  }, []);

  /** Findings for one of the answers this step carries — the caller names which. */
  const errorIn = useCallback(
    (key: Parameters<typeof blockingFor>[1]) => (field: string) =>
      showBlocking ? blockingFor(validation, key, field) : undefined,
    [showBlocking, validation],
  );

  const canContinue = stepComplete(validation, definition);
  const outstanding = blockingCount(validation, definition);
  const isLast = step === STEPS.length - 1;

  /**
   * The mark is uploaded as it is chosen, and the key is what the draft carries.
   *
   * A data URI would survive a reload only as long as this component lives. The bytes have
   * a tenant to belong to now, so they go to object storage and the preview reads them back
   * from there — which also means the preview shows what was actually stored.
   */
  /** Show a mark, revoking whatever was shown before it.
   *
   * A blob URL holds its bytes until it is revoked, so replacing one without revoking it
   * leaks the previous mark for the life of the document. */
  const showMark = (next: string | null) => {
    setMarkPreview((current) => {
      if (current) URL.revokeObjectURL(current);
      return next;
    });
  };

  const chooseMark = async (file: File | null) => {
    if (!file) {
      showMark(null);
      set({ brand_logo_key: null });
      return;
    }
    // Drawn from the file that was chosen rather than fetched back. The bytes are already
    // here, and the upload is content-addressed, so what the server ends up holding is the
    // same bytes by construction — there is nothing a round trip would confirm.
    showMark(URL.createObjectURL(file));
    try {
      set({ brand_logo_key: await uploadMark(tenantId, file) });
    } catch (cause) {
      showMark(null);
      setFailure(cause instanceof ApiError ? cause.message : "The mark could not be uploaded.");
    }
  };

  const attachEvidence = async (files: File[]) => {
    if (files.length === 0) return;
    setEvidenceBusy(true);
    setEvidenceFailure(null);
    try {
      for (const file of files) {
        const entry = await uploadEvidence(file);
        setEvidence((current) => [...current.filter((item) => item.id !== entry.id), entry]);
      }
    } catch (cause) {
      setEvidenceFailure(
        cause instanceof ApiError ? cause.message : "The file could not be attached.",
      );
    } finally {
      setEvidenceBusy(false);
    }
  };

  const detachEvidence = async (id: string) => {
    setEvidenceFailure(null);
    try {
      await deleteEvidence(id);
      setEvidence((current) => current.filter((entry) => entry.id !== id));
    } catch (cause) {
      setEvidenceFailure(
        cause instanceof ApiError ? cause.message : "The file could not be detached.",
      );
    }
  };

  const advance = () => {
    setAttempted((current) => new Set(current).add(step));
    if (!canContinue) return;
    const next = Math.min(step + 1, STEPS.length - 1);
    setStep(next);
    setFurthest((current) => Math.max(current, next));
  };

  const submit = async () => {
    setAttempted((current) => new Set(current).add(step));
    if (validation?.complete !== true) return;
    setSubmitting(true);
    setSubmitFailure(null);
    try {
      setSubmitted(await submitApplication(forValidation, idempotencyKey.current));
    } catch (cause) {
      setSubmitFailure(
        cause instanceof ApiError ? cause.message : "The application could not be submitted.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) return <ApplicationSubmitted application={submitted} />;

  const footnote = () => {
    if (showBlocking && outstanding > 0) {
      return `Complete the required fields on this step to continue — ${outstanding} outstanding.`;
    }
    if (!canContinue) return "Complete the required fields on this step to continue.";
    if (!isLast) return "This step is complete.";
    if (validation?.complete !== true) {
      return "An earlier step is still incomplete. The application is submitted as one payload.";
    }
    return null;
  };

  return (
    <>
      <div className="between">
        <div>
          <div className="eyebrow">Certification body onboarding</div>
          <h1>{definition?.title}</h1>
        </div>
      </div>

      <Stepper current={step} furthest={furthest} validation={validation} onSelect={setStep} />

      {failure ? (
        <p className="problem" role="alert">
          {failure}
        </p>
      ) : null}

      {step === 0 ? (
        <LegalIdentityStep
          draft={draft}
          set={set}
          countries={countries}
          errorFor={errorIn("legal_identity")}
        />
      ) : null}
      {step === 1 ? (
        <AccreditationStep
          draft={draft}
          set={set}
          bodies={bodies}
          standards={standards}
          sectors={sectors}
          errorFor={errorIn("accreditation")}
          onToggleStandard={(standard) => setDraft((current) => toggleStandard(current, standard))}
          onToggleSector={(standard, sector) =>
            setDraft((current) => toggleSector(current, standard, sector))
          }
          onSetSectors={(standard, chosen) =>
            setDraft((current) => setScopeSectors(current, standard, chosen))
          }
          evidence={evidence}
          evidenceBusy={evidenceBusy}
          evidenceFailure={evidenceFailure}
          onUploadEvidence={(files) => void attachEvidence(files)}
          onRemoveEvidence={(id) => void detachEvidence(id)}
        />
      ) : null}
      {step === 2 ? (
        <ImpartialityStep
          draft={draft}
          set={set}
          errorFor={errorIn("impartiality_and_liability")}
        />
      ) : null}
      {step === 3 ? (
        <PeopleStep draft={draft} set={set} errorFor={errorIn("people_and_roles")} />
      ) : null}
      {step === 4 ? (
        <>
          <BrandStep
            draft={draft}
            set={set}
            markPreview={markPreview}
            onMarkChosen={(file) => void chooseMark(file)}
          />
          <PlatformStep
            draft={draft}
            set={set}
            timezones={timezones}
            retentionPolicies={retentionPolicies}
            standards={standards}
            guardrails={guardrails}
            errorFor={errorIn("platform")}
            onToggleEnabled={(standard) => setDraft((current) => toggleEnabled(current, standard))}
          />
        </>
      ) : null}
      {step === 5 ? (
        <AgreementsStep
          draft={draft}
          set={set}
          accreditationBody={draft.accreditation_body ?? ""}
          errorFor={errorIn("agreements")}
        />
      ) : null}

      {/* The brand card renders each of its advisories in place, beside the field that
          caused it, so the shared list would say the same thing a second time. The
          configuration card below it has its own findings and they belong in the list. */}
      <Advisories result={resultFor(validation, definition?.keys.at(-1) ?? null)} />

      {submitFailure ? (
        <p className="problem" role="alert">
          {submitFailure}
        </p>
      ) : null}

      <div className="row" style={{ justifyContent: "space-between" }}>
        <button
          type="button"
          className="btn sec"
          disabled={step === 0}
          onClick={() => setStep((current) => Math.max(0, current - 1))}
        >
          Back
        </button>
        {isLast ? (
          <button type="button" className="btn" disabled={submitting} onClick={() => void submit()}>
            {submitting ? "Submitting…" : "Submit for verification"}
          </button>
        ) : (
          <button type="button" className="btn" onClick={advance}>
            Continue
          </button>
        )}
      </div>

      {footnote() ? <p className="foot">{footnote()}</p> : null}
    </>
  );
}
